import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Menu, X, Phone, Mail, MapPin, Facebook, Twitter, Youtube } from 'lucide-react';
import i18n from 'i18next';
import Cookies from 'js-cookie';
import { CHANGE_LANG } from 'context/actions/system';

// URL үүсгэх
const pageHref = (page) => {
    if (page.externalUrl) return page.externalUrl;
    if (page.url) {
        return page.url.startsWith('/') ? page.url : '/' + page.url;
    }
    return `/content/${page.id}`;
};

// Хэлээс хамаарч нэр сонгох
const pickName = (page, lang) => {
    if (lang === 'en' && page.nameEn) return page.nameEn;
    return page.name || '';
};

const isExternal = (page) => !!page.externalUrl;
const hasKids = (page) => Array.isArray(page.children) && page.children.length > 0;

/* ═══════════════════════════════════════════════════════════════
   DESKTOP: Recursive dropdown item
   - Эхний түвшин (depth=0): доош задрана (top-full)
   - 2+ түвшин: баруун талд гарна (left-full).
     Дэлгэцийн баруун ирмэг хэтэрвэл openLeft prop-р зүүн тийш эргэнэ.
   ═══════════════════════════════════════════════════════════════ */
const DesktopMenuItem = ({ page, lang, depth = 0 }) => {
    const [open, setOpen] = useState(false);
    const [openLeft, setOpenLeft] = useState(false);
    const wrapperRef = useRef(null);
    const label = pickName(page, lang);
    const children = hasKids(page);

    // Sub-menu гарахын өмнө баруун ирмэг хэтэрсэн эсэхийг шалгана
    useEffect(() => {
        if (!open || depth === 0 || !wrapperRef.current) return;
        const rect = wrapperRef.current.getBoundingClientRect();
        const submenuWidth = 260;
        const wouldOverflow = rect.right + submenuWidth > window.innerWidth - 8;
        setOpenLeft(wouldOverflow);
    }, [open, depth]);

    // Хэрэв children байхгүй бол — энгийн link/external
    if (!children) {
        const baseLink =
            depth === 0
                ? 'block px-3 py-2 text-[12px] font-semibold uppercase tracking-wide text-white hover:text-brand-yellow transition'
                : 'block px-5 py-2.5 text-[13px] text-brand-dark hover:bg-brand-cream hover:text-brand-yellow-dark transition';

        if (isExternal(page)) {
            return (
                <a
                    href={pageHref(page)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={baseLink}
                >
                    {label}
                    <span className="ml-1 text-[10px] opacity-50">↗</span>
                </a>
            );
        }
        return (
            <Link href={pageHref(page)} className={baseLink}>
                {label}
            </Link>
        );
    }

    // Children-тэй — dropdown гаргана
    // Submenu-ийн position: эхний түвшинд доош, бусдад баруун/зүүн талаар
    const submenuPosition =
        depth === 0
            ? 'top-full left-0 border-t-2 border-brand-yellow'
            : openLeft
              ? 'top-0 right-full border-r-2 border-brand-yellow'
              : 'top-0 left-full border-l-2 border-brand-yellow';

    // Trigger-ийн style depth-ээс хамаарна
    const triggerCls =
        depth === 0
            ? `flex items-center gap-1 px-3 py-2 text-[12px] font-semibold uppercase tracking-wide transition ${
                  open ? 'text-brand-yellow' : 'text-white hover:text-brand-yellow'
              }`
            : `flex items-center justify-between gap-2 w-full px-5 py-2.5 text-[13px] text-left transition ${
                  open
                      ? 'bg-brand-cream text-brand-yellow-dark'
                      : 'text-brand-dark hover:bg-brand-cream hover:text-brand-yellow-dark'
              }`;

    const ChevIcon = depth === 0 ? ChevronDown : ChevronRight;

    return (
        <div
            ref={wrapperRef}
            className="relative"
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
        >
            <button className={triggerCls}>
                {label}
                <ChevIcon
                    size={depth === 0 ? 12 : 14}
                    className={`transition-transform ${
                        depth === 0 && open ? 'rotate-180' : ''
                    }`}
                />
            </button>
            {open && (
                <div
                    className={`absolute min-w-[260px] bg-white shadow-2xl animate-fade-in-fast ${submenuPosition}`}
                >
                    <ul className="py-2">
                        {page.children.map((child) => (
                            <li key={child.id}>
                                <DesktopMenuItem
                                    page={child}
                                    lang={lang}
                                    depth={depth + 1}
                                />
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

/* ═══════════════════════════════════════════════════════════════
   MOBILE: Recursive accordion item
   - Children-тэй бол click → expand
   - Indent: depth * 12px
   ═══════════════════════════════════════════════════════════════ */
const MobileMenuItem = ({ page, lang, depth = 0 }) => {
    const [open, setOpen] = useState(false);
    const label = pickName(page, lang);
    const children = hasKids(page);

    // Indent depth-ээс хамаарч (Tailwind dynamic class style)
    const indentStyle = { paddingLeft: `${depth * 16 + 8}px` };

    // Эхний түвшин (depth=0): том, цагаан, uppercase
    // 2+ түвшин: жижиг, илүү цайвар
    const isTop = depth === 0;

    const linkBase = isTop
        ? 'flex items-center justify-between py-4 text-sm font-bold uppercase tracking-wider text-white hover:text-brand-yellow transition'
        : 'flex items-center justify-between py-2.5 text-sm text-white/70 hover:text-brand-yellow transition';

    // Children байхгүй — энгийн link
    if (!children) {
        const content = (
            <>
                {!isTop && (
                    <span className="w-1 h-1 rounded-full bg-brand-yellow flex-shrink-0 mr-2 inline-block" />
                )}
                <span className="flex-1">{label}</span>
                {isExternal(page) && (
                    <span className="text-[10px] opacity-60 ml-1">↗</span>
                )}
            </>
        );

        if (isExternal(page)) {
            return (
                <a
                    href={pageHref(page)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={linkBase}
                    style={indentStyle}
                >
                    {content}
                </a>
            );
        }
        return (
            <Link href={pageHref(page)} className={linkBase} style={indentStyle}>
                {content}
            </Link>
        );
    }

    // Children-тэй — accordion trigger
    return (
        <div className={isTop ? 'border-b border-white/10 last:border-0' : ''}>
            <button
                onClick={() => setOpen((v) => !v)}
                className={`w-full ${linkBase}`}
                style={indentStyle}
                aria-expanded={open}
            >
                <span className="flex items-center flex-1">
                    {!isTop && (
                        <span className="w-1 h-1 rounded-full bg-brand-yellow flex-shrink-0 mr-2" />
                    )}
                    {label}
                </span>
                <ChevronDown
                    size={isTop ? 18 : 14}
                    className={`transition-transform flex-shrink-0 ml-2 ${
                        open ? 'rotate-180' : ''
                    }`}
                />
            </button>
            {open && (
                <div className={isTop ? 'pb-2' : 'pb-1'}>
                    {page.children.map((child) => (
                        <MobileMenuItem
                            key={child.id}
                            page={child}
                            lang={lang}
                            depth={depth + 1}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

/* ═══════════════════════════════════════════════════════════════
   HEADER (main)
   ═══════════════════════════════════════════════════════════════ */
const Header = () => {
    const { t } = useTranslation();
    const router = useRouter();
    const dispatch = useDispatch();
    const userMenus = useSelector((s) => s.system.userMenus || []);
    const companyInfo = useSelector((s) => s.system.companyInfo || {});
    const lang = useSelector((s) => s.system.lang || 'mn');

    const [scrolled, setScrolled] = useState(false);
    const [mounted, setMounted] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => setMounted(true), 100);
        const onScroll = () => setScrolled(window.scrollY > 50);
        onScroll();
        window.addEventListener('scroll', onScroll);
        return () => {
            clearTimeout(timer);
            window.removeEventListener('scroll', onScroll);
        };
    }, [router.pathname]);

    useEffect(() => {
        const handleRouteChange = () => setMobileOpen(false);
        router.events.on('routeChangeStart', handleRouteChange);
        return () => router.events.off('routeChangeStart', handleRouteChange);
    }, [router.events]);

    const changeLanguage = (l) => {
        i18n.changeLanguage(l);
        dispatch({ type: CHANGE_LANG, data: l });
        Cookies.set('chosen_lang', l, { expires: 365 });
    };

    const isHomePage = router.pathname === '/';
    const pageCover = useSelector((s) => s.system.pageCover);
    const isOverlayMode = isHomePage || pageCover;
    const headerBg =
        scrolled || !isOverlayMode
            ? 'bg-brand-dark shadow-lg'
            : 'bg-transparent';

    return (
        <div
            className={`fixed top-0 left-0 right-0 z-50 w-full transition-transform duration-700 ease-out ${
                mounted ? 'translate-y-0' : '-translate-y-full'
            }`}
        >
            {/* ═══════════════════════════════════════════════════════
                TOP BAR (URD §2.7) — хаяг, утас, имэйл, хэл солих
                ═══════════════════════════════════════════════════════ */}
            <div
                className={`text-white/80 text-xs hidden md:block transition-all duration-300 ${
                    scrolled || !isOverlayMode
                        ? 'bg-brand-dark-lighter'
                        : 'bg-brand-dark/60 backdrop-blur-sm'
                }`}
            >
                <div className="max-w-[1600px] mx-auto px-4 lg:px-8">
                    <div className="flex items-center justify-between h-9">
                        <div className="flex items-center gap-6">
                            {companyInfo.address && (
                                <a
                                    href={`https://maps.google.com/?q=${encodeURIComponent(companyInfo.address)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-1.5 hover:text-brand-yellow transition"
                                >
                                    <MapPin size={12} className="text-brand-yellow" />
                                    <span className="hidden lg:inline">
                                        {companyInfo.address}
                                    </span>
                                </a>
                            )}
                            {companyInfo.phone && (
                                <a
                                    href={`tel:${companyInfo.phone.replace(/\s/g, '')}`}
                                    className="flex items-center gap-1.5 hover:text-brand-yellow transition"
                                >
                                    <Phone size={12} className="text-brand-yellow" />
                                    {companyInfo.phone}
                                </a>
                            )}
                            {companyInfo.email && (
                                <a
                                    href={`mailto:${companyInfo.email}`}
                                    className="flex items-center gap-1.5 hover:text-brand-yellow transition"
                                >
                                    <Mail size={12} className="text-brand-yellow" />
                                    {companyInfo.email}
                                </a>
                            )}
                        </div>

                        {/* Social icons + MN | EN switcher */}
                        <div className="flex items-center gap-4">
                            {(companyInfo.facebook ||
                                companyInfo.twitter ||
                                companyInfo.youtube) && (
                                <div className="flex items-center gap-3">
                                    {companyInfo.facebook && (
                                        <a
                                            href={companyInfo.facebook}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label="Facebook"
                                            className="text-white/60 hover:text-brand-yellow transition"
                                        >
                                            <Facebook size={14} />
                                        </a>
                                    )}
                                    {companyInfo.twitter && (
                                        <a
                                            href={companyInfo.twitter}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label="Twitter"
                                            className="text-white/60 hover:text-brand-yellow transition"
                                        >
                                            <Twitter size={14} />
                                        </a>
                                    )}
                                    {companyInfo.youtube && (
                                        <a
                                            href={companyInfo.youtube}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label="YouTube"
                                            className="text-white/60 hover:text-brand-yellow transition"
                                        >
                                            <Youtube size={14} />
                                        </a>
                                    )}
                                    <span className="text-white/30">|</span>
                                </div>
                            )}

                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => changeLanguage('mn')}
                                    className={`px-2 py-0.5 text-xs font-bold transition ${
                                        lang === 'mn'
                                            ? 'text-brand-yellow'
                                            : 'text-white/50 hover:text-white'
                                    }`}
                                >
                                    MN
                                </button>
                                <span className="text-white/30">|</span>
                                <button
                                    onClick={() => changeLanguage('en')}
                                    className={`px-2 py-0.5 text-xs font-bold transition ${
                                        lang === 'en'
                                            ? 'text-brand-yellow'
                                            : 'text-white/50 hover:text-white'
                                    }`}
                                >
                                    EN
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ═══════════════════════════════════════════════════════
                MAIN HEADER
                ═══════════════════════════════════════════════════════ */}
            <header
                className={`transition-all duration-300 ${headerBg}`}
            >
                <div className="max-w-[1600px] mx-auto px-4 lg:px-8">
                    <div className="flex items-center justify-between h-16 lg:h-20">
                        {/* Лого */}
                        <Link href="/" className="flex items-center flex-shrink-0">
                            <Image
                                src="/logo.png"
                                alt="Mongol Refinery"
                                width={180}
                                height={56}
                                className="object-contain h-10 lg:h-14 w-auto"
                                priority
                            />
                        </Link>

                        {/* ═══ Desktop Menu (recursive) ═══ */}
                        <nav className="hidden lg:flex items-center gap-0.5">
                            {userMenus.map((page) => (
                                <DesktopMenuItem
                                    key={page.id}
                                    page={page}
                                    lang={lang}
                                    depth={0}
                                />
                            ))}
                        </nav>

                        {/* ═══ Mobile: MN/EN + hamburger ═══ */}
                        <div className="flex items-center gap-2 lg:hidden">
                            <div className="flex items-center gap-1 text-xs">
                                <button
                                    onClick={() => changeLanguage('mn')}
                                    className={`px-1.5 py-0.5 font-bold ${
                                        lang === 'mn'
                                            ? 'text-brand-yellow'
                                            : 'text-white/60'
                                    }`}
                                >
                                    MN
                                </button>
                                <span className="text-white/30">|</span>
                                <button
                                    onClick={() => changeLanguage('en')}
                                    className={`px-1.5 py-0.5 font-bold ${
                                        lang === 'en'
                                            ? 'text-brand-yellow'
                                            : 'text-white/60'
                                    }`}
                                >
                                    EN
                                </button>
                            </div>
                            <button
                                onClick={() => setMobileOpen(!mobileOpen)}
                                className="p-2 text-white"
                                aria-label="Menu"
                            >
                                {mobileOpen ? <X size={24} /> : <Menu size={24} />}
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {/* ═══════════════════════════════════════════════════════
                MOBILE DRAWER (URD F-02: accordion) — recursive
                ═══════════════════════════════════════════════════════ */}
            {mounted &&
                createPortal(
                    <>
                        {/* Backdrop */}
                        <div
                            className={`lg:hidden fixed inset-0 z-[60] bg-black/60 transition-opacity duration-300 ${
                                mobileOpen
                                    ? 'opacity-100 pointer-events-auto'
                                    : 'opacity-0 pointer-events-none'
                            }`}
                            onClick={() => setMobileOpen(false)}
                        />

                        {/* Drawer */}
                        <div
                            className={`lg:hidden fixed top-0 right-0 z-[70] h-full w-[90%] max-w-[400px] bg-brand-dark flex flex-col transition-transform duration-300 ease-in-out ${
                                mobileOpen ? 'translate-x-0' : 'translate-x-full'
                            }`}
                        >
                            {/* Header */}
                            <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
                                <Image
                                    src="/logo.png"
                                    alt="Mongol Refinery"
                                    width={140}
                                    height={44}
                                    className="object-contain h-10 w-auto"
                                />
                                <button
                                    onClick={() => setMobileOpen(false)}
                                    className="p-2 text-white/70 hover:text-white"
                                    aria-label="Хаах"
                                >
                                    <X size={26} />
                                </button>
                            </div>

                            {/* Цэс (recursive) */}
                            <nav className="flex-1 overflow-y-auto px-4 py-4">
                                {userMenus.map((page) => (
                                    <MobileMenuItem
                                        key={page.id}
                                        page={page}
                                        lang={lang}
                                        depth={0}
                                    />
                                ))}
                            </nav>

                            {/* Contact info доод хэсэг */}
                            <div className="px-6 py-5 border-t border-white/10 space-y-2 text-xs text-white/60">
                                {companyInfo.phone && (
                                    <a
                                        href={`tel:${companyInfo.phone.replace(/\s/g, '')}`}
                                        className="flex items-center gap-2 hover:text-brand-yellow"
                                    >
                                        <Phone size={12} className="text-brand-yellow" />
                                        {companyInfo.phone}
                                    </a>
                                )}
                                {companyInfo.email && (
                                    <a
                                        href={`mailto:${companyInfo.email}`}
                                        className="flex items-center gap-2 hover:text-brand-yellow"
                                    >
                                        <Mail size={12} className="text-brand-yellow" />
                                        {companyInfo.email}
                                    </a>
                                )}
                            </div>
                        </div>
                    </>,
                    document.body
                )}
        </div>
    );
};

export default Header;